import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, createVNode as _createVNode, toDisplayString as _toDisplayString, createTextVNode as _createTextVNode, withCtx as _withCtx, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, renderList as _renderList, Fragment as _Fragment, createElementBlock as _createElementBlock, createElementVNode as _createElementVNode, normalizeClass as _normalizeClass } from "vue"

const _hoisted_1 = {
  key: 0,
  class: "p-6 space-y-4"
}
const _hoisted_2 = {
  key: 1,
  class: "p-6 text-center text-muted-foreground"
}
const _hoisted_3 = {
  key: 2,
  class: "pb-2"
}
const _hoisted_4 = {
  key: 0,
  class: "p-4"
}
const _hoisted_5 = { class: "flex items-start justify-between gap-2 mb-2" }
const _hoisted_6 = { class: "text-sm font-semibold leading-tight" }
const _hoisted_7 = { class: "space-y-1.5 text-xs text-muted-foreground" }
const _hoisted_8 = { class: "flex items-center gap-1.5" }
const _hoisted_9 = {
  key: 0,
  class: "flex items-center gap-1.5"
}
const _hoisted_10 = {
  key: 1,
  class: "flex items-center gap-1.5"
}
const _hoisted_11 = {
  key: 2,
  class: "flex items-center gap-1.5"
}
const _hoisted_12 = { class: "flex items-center gap-2 pt-1" }
const _hoisted_13 = {
  key: 0,
  class: "text-muted-foreground truncate"
}
const _hoisted_14 = {
  key: 1,
  class: "px-4 pb-2"
}
const _hoisted_15 = { class: "space-y-1" }
const _hoisted_16 = ["onClick"]
const _hoisted_17 = { class: "h-8 w-8 rounded-full bg-primary/10 flex items-center justify-center shrink-0" }
const _hoisted_18 = { class: "flex-1 min-w-0" }
const _hoisted_19 = { class: "text-sm font-medium truncate" }
const _hoisted_20 = { class: "text-xs text-muted-foreground" }
const _hoisted_21 = {
  key: 0,
  class: "text-xs text-muted-foreground/70 truncate flex items-center gap-1"
}
const _hoisted_22 = {
  key: 2,
  class: "px-4 pb-2"
}
const _hoisted_23 = { class: "flex gap-1 mb-3" }
const _hoisted_24 = { class: "flex items-center gap-2 mb-3" }
const _hoisted_25 = { class: "space-y-1" }
const _hoisted_26 = ["onClick"]
const _hoisted_27 = { class: "h-8 w-8 rounded-full bg-muted flex items-center justify-center shrink-0" }
const _hoisted_28 = { class: "flex-1 min-w-0" }
const _hoisted_29 = { class: "text-sm truncate" }
const _hoisted_30 = { class: "text-xs text-muted-foreground" }
const _hoisted_31 = {
  key: 0,
  class: "text-xs text-muted-foreground text-center py-3"
}
const _hoisted_32 = { class: "p-4" }

import { ref, computed, watch } from 'vue'
import { useRouter } from 'vue-router'
import { useMeetingsStore } from '@/views/Meetings/store'
import { useProjectsStore } from '@/views/Projects/store'
import { useApplicationsStore } from '@/views/Applications/store'
import { formatDate } from '@/utils/formatters'
import { getMeetingStatusLabel, getMeetingTypeLabel } from '@/types/enums'
import type { Meeting } from '@/types'

import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { ScrollArea } from '@/components/ui/scroll-area'
import { Separator } from '@/components/ui/separator'
import { Input } from '@/components/ui/input'
import {
  CalendarDays,
  Clock,
  MapPin,
  Users,
  ChevronRight,
  CalendarCheck,
  FolderKanban,
} from 'lucide-vue-next'


export default /*@__PURE__*/_defineComponent({
  __name: 'MeetingsWidget',
  props: {
    open: { type: Boolean }
  },
  emits: ["update:open"],
  setup(__props: any, { emit: __emit }) {

const props = __props

const emit = __emit

const router = useRouter()
const meetingsStore = useMeetingsStore()
const projectsStore = useProjectsStore()
const applicationsStore = useApplicationsStore()

const pastFilter = ref<'all' | 'completed' | 'cancelled'>('all')
const pastDateFrom = ref('')
const pastDateTo = ref('')

watch(() => props.open, async (isOpen) => {
  if (isOpen && meetingsStore.meetings.length === 0) {
    await meetingsStore.fetchMeetings()
  }
  if (isOpen && projectsStore.projects.length === 0) {
    await projectsStore.fetchProjects()
  }
  if (isOpen && applicationsStore.applications.length === 0) {
    await applicationsStore.fetchApplications()
  }
})

const nextMeeting = computed(() => {
  return meetingsStore.upcomingMeetings[0] ?? null
})

const upcomingList = computed(() => {
  return meetingsStore.upcomingMeetings.slice(1, 6)
})

const pastMeetings = computed(() => {
  const now = new Date()
  let past = meetingsStore.meetings.filter((m) => {
    if (m.status === 'completed' || m.status === 'cancelled') return true
    if (m.status === 'scheduled' && new Date(m.scheduledDate) < now) return true
    return false
  })

  if (pastFilter.value !== 'all') {
    past = past.filter((m) => m.status === pastFilter.value)
  }

  if (pastDateFrom.value) {
    const from = new Date(pastDateFrom.value + 'T00:00:00')
    past = past.filter((m) => new Date(m.scheduledDate) >= from)
  }

  if (pastDateTo.value) {
    const to = new Date(pastDateTo.value + 'T23:59:59')
    past = past.filter((m) => new Date(m.scheduledDate) <= to)
  }

  return past
    .sort((a, b) => new Date(b.scheduledDate).getTime() - new Date(a.scheduledDate).getTime())
    .slice(0, 10)
})

const hasDateFilter = computed(() => !!pastDateFrom.value || !!pastDateTo.value)

const clearDateFilter = () => {
  pastDateFrom.value = ''
  pastDateTo.value = ''
}

const getProjectName = (projectId: string): string => {
  const project = projectsStore.getProjectById(projectId)
  return project?.name ?? ''
}

const getAppName = (applicationId?: string): string => {
  if (!applicationId) return ''
  const app = applicationsStore.applications.find((a) => a.id === applicationId)
  return app?.name ?? ''
}

const getStatusVariant = (status: string): 'default' | 'secondary' | 'destructive' | 'outline' => {
  switch (status) {
    case 'completed': return 'secondary'
    case 'cancelled': return 'destructive'
    case 'in_progress': return 'default'
    case 'postponed': return 'outline'
    default: return 'default'
  }
}

const goToMeeting = (meeting: Meeting) => {
  emit('update:open', false)
  router.push({ name: 'meeting-detail', params: { id: meeting.projectId, meetingId: meeting.id } })
}

const goToCalendar = () => {
  emit('update:open', false)
  router.push('/calendar')
}

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createBlock(_unref(Sheet), {
    open: props.open,
    "onUpdate:open": _cache[6] || (_cache[6] = ($event: any) => (emit('update:open', $event)))
  }, {
    default: _withCtx(() => [
      _createVNode(_unref(SheetContent), {
        side: "right",
        class: "w-full sm:max-w-md p-0 flex flex-col"
      }, {
        default: _withCtx(() => [
          _createVNode(_unref(SheetHeader), { class: "px-6 py-4 border-b" }, {
            default: _withCtx(() => [
              _createVNode(_unref(SheetTitle), { class: "flex items-center gap-2" }, {
                default: _withCtx(() => [
                  _createVNode(_unref(CalendarDays), { class: "h-5 w-5" }),
                  _cache[7] || (_cache[7] = _createTextVNode(" Reuniones ", -1)),
                  (_unref(meetingsStore).upcomingMeetings.length > 0)
                    ? (_openBlock(), _createBlock(_unref(Badge), {
                        key: 0,
                        variant: "default",
                        class: "ml-1"
                      }, {
                        default: _withCtx(() => [
                          _createTextVNode(_toDisplayString(_unref(meetingsStore).upcomingMeetings.length), 1)
                        ]),
                        _: 1
                      }))
                    : _createCommentVNode("", true)
                ]),
                _: 1
              })
            ]),
            _: 1
          }),
          _createVNode(_unref(ScrollArea), { class: "flex-1" }, {
            default: _withCtx(() => [
              (_unref(meetingsStore).isLoading)
                ? (_openBlock(), _createElementBlock("div", _hoisted_1, [
                    (_openBlock(), _createElementBlock(_Fragment, null, _renderList(3, (i) => {
                      return _createElementVNode("div", {
                        key: i,
                        class: "space-y-2"
                      }, [...(_cache[8] || (_cache[8] = [
                        _createElementVNode("div", { class: "h-4 bg-muted animate-pulse rounded w-3/4" }, null, -1),
                        _createElementVNode("div", { class: "h-3 bg-muted animate-pulse rounded w-1/2" }, null, -1)
                      ]))])
                    }), 64))
                  ]))
                : (_unref(meetingsStore).meetings.length === 0)
                  ? (_openBlock(), _createElementBlock("div", _hoisted_2, [
                      _createVNode(_unref(CalendarDays), { class: "h-12 w-12 mx-auto mb-3 opacity-50" }),
                      _cache[9] || (_cache[9] = _createElementVNode("p", null, "No hay reuniones programadas", -1))
                    ]))
                  : (_openBlock(), _createElementBlock("div", _hoisted_3, [
                      (nextMeeting.value)
                        ? (_openBlock(), _createElementBlock("div", _hoisted_4, [
                            _cache[10] || (_cache[10] = _createElementVNode("p", { class: "text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-3" }, " Proxima reunion ", -1)),
                            _createElementVNode("div", {
                              class: "border border-primary rounded-lg bg-primary/5 p-4 cursor-pointer hover:bg-primary/10 transition-colors",
                              onClick: _cache[0] || (_cache[0] = ($event: any) => (goToMeeting(nextMeeting.value)))
                            }, [
                              _createElementVNode("div", _hoisted_5, [
                                _createElementVNode("h3", _hoisted_6, _toDisplayString(nextMeeting.value.title), 1),
                                _createVNode(_unref(Badge), {
                                  variant: getStatusVariant(nextMeeting.value.status),
                                  class: "shrink-0 text-[10px]"
                                }, {
                                  default: _withCtx(() => [
                                    _createTextVNode(_toDisplayString(_unref(getMeetingStatusLabel)(nextMeeting.value.status)), 1)
                                  ]),
                                  _: 1
                                }, 8, ["variant"])
                              ]),
                              _createElementVNode("div", _hoisted_7, [
                                _createElementVNode("div", _hoisted_8, [
                                  _createVNode(_unref(CalendarDays), { class: "h-3.5 w-3.5" }),
                                  _createTextVNode(" " + _toDisplayString(_unref(formatDate)(nextMeeting.value.scheduledDate, 'long')), 1)
                                ]),
                                (nextMeeting.value.startTime)
                                  ? (_openBlock(), _createElementBlock("div", _hoisted_9, [
                                      _createVNode(_unref(Clock), { class: "h-3.5 w-3.5" }),
                                      _createTextVNode(" " + _toDisplayString(nextMeeting.value.startTime) + " ", 1),
                                      (nextMeeting.value.endTime)
                                        ? (_openBlock(), _createElementBlock(_Fragment, { key: 0 }, [
                                            _createTextVNode(" - " + _toDisplayString(nextMeeting.value.endTime), 1)
                                          ], 64))
                                        : _createCommentVNode("", true)
                                    ]))
                                  : _createCommentVNode("", true),
                                (nextMeeting.value.location)
                                  ? (_openBlock(), _createElementBlock("div", _hoisted_10, [
                                      _createVNode(_unref(MapPin), { class: "h-3.5 w-3.5" }),
                                      _createTextVNode(" " + _toDisplayString(nextMeeting.value.location), 1)
                                    ]))
                                  : _createCommentVNode("", true),
                                (nextMeeting.value.attendees.length > 0)
                                  ? (_openBlock(), _createElementBlock("div", _hoisted_11, [
                                      _createVNode(_unref(Users), { class: "h-3.5 w-3.5" }),
                                      _createTextVNode(" " + _toDisplayString(nextMeeting.value.attendees.length) + " asistentes ", 1)
                                    ]))
                                  : _createCommentVNode("", true),
                                _createElementVNode("div", _hoisted_12, [
                                  _createVNode(_unref(Badge), {
                                    variant: "outline",
                                    class: "text-[10px]"
                                  }, {
                                    default: _withCtx(() => [
                                      _createTextVNode(_toDisplayString(_unref(getMeetingTypeLabel)(nextMeeting.value.meetingType)), 1)
                                    ]),
                                    _: 1
                                  }),
                                  (getProjectName(nextMeeting.value.projectId))
                                    ? (_openBlock(), _createElementBlock("span", _hoisted_13, [
                                        _createTextVNode(_toDisplayString(getProjectName(nextMeeting.value.projectId)) + " ", 1),
                                        (getAppName(nextMeeting.value.applicationId))
                                          ? (_openBlock(), _createElementBlock(_Fragment, { key: 0 }, [
                                              _createTextVNode(" · " + _toDisplayString(getAppName(nextMeeting.value.applicationId)), 1)
                                            ], 64))
                                          : _createCommentVNode("", true)
                                      ]))
                                    : _createCommentVNode("", true)
                                ])
                              ])
                            ])
                          ]))
                        : _createCommentVNode("", true),
                      (upcomingList.value.length > 0)
                        ? (_openBlock(), _createElementBlock("div", _hoisted_14, [
                            _createVNode(_unref(Separator), { class: "mb-3" }),
                            _cache[11] || (_cache[11] = _createElementVNode("p", { class: "text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-3" }, " Proximas reuniones ", -1)),
                            _createElementVNode("div", _hoisted_15, [
                              (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(upcomingList.value, (meeting) => {
                                return (_openBlock(), _createElementBlock("div", {
                                  key: meeting.id,
                                  class: "flex items-center gap-3 p-2 rounded-md cursor-pointer hover:bg-accent/50 transition-colors",
                                  onClick: ($event: any) => (goToMeeting(meeting))
                                }, [
                                  _createElementVNode("div", _hoisted_17, [
                                    _createVNode(_unref(CalendarCheck), { class: "h-4 w-4 text-primary" })
                                  ]),
                                  _createElementVNode("div", _hoisted_18, [
                                    _createElementVNode("p", _hoisted_19, _toDisplayString(meeting.title), 1),
                                    _createElementVNode("p", _hoisted_20, [
                                      _createTextVNode(_toDisplayString(_unref(formatDate)(meeting.scheduledDate, 'short')) + " ", 1),
                                      (meeting.startTime)
                                        ? (_openBlock(), _createElementBlock(_Fragment, { key: 0 }, [
                                            _createTextVNode(" · " + _toDisplayString(meeting.startTime), 1)
                                          ], 64))
                                        : _createCommentVNode("", true)
                                    ]),
                                    (getProjectName(meeting.projectId))
                                      ? (_openBlock(), _createElementBlock("p", _hoisted_21, [
                                          _createVNode(_unref(FolderKanban), { class: "h-3 w-3 inline shrink-0" }),
                                          _createTextVNode(" " + _toDisplayString(getProjectName(meeting.projectId)) + " ", 1),
                                          (getAppName(meeting.applicationId))
                                            ? (_openBlock(), _createElementBlock(_Fragment, { key: 0 }, [
                                                _createTextVNode(" · " + _toDisplayString(getAppName(meeting.applicationId)), 1)
                                              ], 64))
                                            : _createCommentVNode("", true)
                                        ]))
                                      : _createCommentVNode("", true)
                                  ]),
                                  _createVNode(_unref(ChevronRight), { class: "h-4 w-4 text-muted-foreground shrink-0" })
                                ], 8, _hoisted_16))
                              }), 128))
                            ])
                          ]))
                        : _createCommentVNode("", true),
                      (pastMeetings.value.length > 0 || pastFilter.value !== 'all' || hasDateFilter.value)
                        ? (_openBlock(), _createElementBlock("div", _hoisted_22, [
                            _createVNode(_unref(Separator), { class: "mb-3" }),
                            _cache[17] || (_cache[17] = _createElementVNode("div", { class: "flex items-center justify-between mb-3" }, [
                              _createElementVNode("p", { class: "text-xs font-semibold text-muted-foreground uppercase tracking-wider" }, " Reuniones pasadas ")
                            ], -1)),
                            _createElementVNode("div", _hoisted_23, [
                              _createVNode(_unref(Button), {
                                variant: "ghost",
                                size: "sm",
                                class: _normalizeClass(["h-7 text-xs px-2.5", { 'bg-accent': pastFilter.value === 'all' }]),
                                onClick: _cache[1] || (_cache[1] = ($event: any) => (pastFilter.value = 'all'))
                              }, {
                                default: _withCtx(() => [...(_cache[12] || (_cache[12] = [
                                  _createTextVNode(" Todas ", -1)
                                ]))]),
                                _: 1
                              }, 8, ["class"]),
                              _createVNode(_unref(Button), {
                                variant: "ghost",
                                size: "sm",
                                class: _normalizeClass(["h-7 text-xs px-2.5", { 'bg-accent': pastFilter.value === 'completed' }]),
                                onClick: _cache[2] || (_cache[2] = ($event: any) => (pastFilter.value = 'completed'))
                              }, {
                                default: _withCtx(() => [...(_cache[13] || (_cache[13] = [
                                  _createTextVNode(" Completadas ", -1)
                                ]))]),
                                _: 1
                              }, 8, ["class"]),
                              _createVNode(_unref(Button), {
                                variant: "ghost",
                                size: "sm",
                                class: _normalizeClass(["h-7 text-xs px-2.5", { 'bg-accent': pastFilter.value === 'cancelled' }]),
                                onClick: _cache[3] || (_cache[3] = ($event: any) => (pastFilter.value = 'cancelled'))
                              }, {
                                default: _withCtx(() => [...(_cache[14] || (_cache[14] = [
                                  _createTextVNode(" Canceladas ", -1)
                                ]))]),
                                _: 1
                              }, 8, ["class"])
                            ]),
                            _createElementVNode("div", _hoisted_24, [
                              _createVNode(_unref(Input), {
                                modelValue: pastDateFrom.value,
                                "onUpdate:modelValue": _cache[4] || (_cache[4] = ($event: any) => ((pastDateFrom).value = $event)),
                                type: "date",
                                class: "h-7 text-xs flex-1",
                                placeholder: "Desde"
                              }, null, 8, ["modelValue"]),
                              _cache[16] || (_cache[16] = _createElementVNode("span", { class: "text-xs text-muted-foreground" }, "-", -1)),
                              _createVNode(_unref(Input), {
                                modelValue: pastDateTo.value,
                                "onUpdate:modelValue": _cache[5] || (_cache[5] = ($event: any) => ((pastDateTo).value = $event)),
                                type: "date",
                                class: "h-7 text-xs flex-1",
                                placeholder: "Hasta"
                              }, null, 8, ["modelValue"]),
                              (hasDateFilter.value)
                                ? (_openBlock(), _createBlock(_unref(Button), {
                                    key: 0,
                                    variant: "ghost",
                                    size: "sm",
                                    class: "h-7 text-xs px-2",
                                    onClick: clearDateFilter
                                  }, {
                                    default: _withCtx(() => [...(_cache[15] || (_cache[15] = [
                                      _createTextVNode(" Limpiar ", -1)
                                    ]))]),
                                    _: 1
                                  }))
                                : _createCommentVNode("", true)
                            ]),
                            _createElementVNode("div", _hoisted_25, [
                              (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(pastMeetings.value, (meeting) => {
                                return (_openBlock(), _createElementBlock("div", {
                                  key: meeting.id,
                                  class: "flex items-center gap-3 p-2 rounded-md cursor-pointer hover:bg-accent/50 transition-colors",
                                  onClick: ($event: any) => (goToMeeting(meeting))
                                }, [
                                  _createElementVNode("div", _hoisted_27, [
                                    _createVNode(_unref(CalendarDays), { class: "h-4 w-4 text-muted-foreground" })
                                  ]),
                                  _createElementVNode("div", _hoisted_28, [
                                    _createElementVNode("p", _hoisted_29, _toDisplayString(meeting.title), 1),
                                    _createElementVNode("p", _hoisted_30, _toDisplayString(_unref(formatDate)(meeting.scheduledDate, 'short')), 1)
                                  ]),
                                  _createVNode(_unref(Badge), {
                                    variant: getStatusVariant(meeting.status),
                                    class: "shrink-0 text-[10px]"
                                  }, {
                                    default: _withCtx(() => [
                                      _createTextVNode(_toDisplayString(_unref(getMeetingStatusLabel)(meeting.status)), 1)
                                    ]),
                                    _: 2
                                  }, 1032, ["variant"])
                                ], 8, _hoisted_26))
                              }), 128))
                            ]),
                            (pastMeetings.value.length === 0)
                              ? (_openBlock(), _createElementBlock("p", _hoisted_31, " No hay reuniones con este filtro "))
                              : _createCommentVNode("", true)
                          ]))
                        : _createCommentVNode("", true)
                    ]))
            ]),
            _: 1
          }),
          _createVNode(_unref(Separator)),
          _createElementVNode("div", _hoisted_32, [
            _createVNode(_unref(Button), {
              variant: "outline",
              class: "w-full",
              onClick: goToCalendar
            }, {
              default: _withCtx(() => [...(_cache[18] || (_cache[18] = [
                _createTextVNode(" Ver calendario completo ", -1)
              ]))]),
              _: 1
            })
          ])
        ]),
        _: 1
      })
    ]),
    _: 1
  }, 8, ["open"]))
}
}

})